/**
 * Plain-language microcopy cho từng nhóm/key cấu hình. Dùng ở Config landing,
 * SeasonsConfig, BulkNormsConfig để giải thích "key này là gì, dùng làm gì,
 * ví dụ cụ thể" — giảm phụ thuộc vào tài liệu ngoài.
 */
import type { ConfigKind } from './config';

export interface ConfigCategoryHelp {
  label: string;
  why: string;
  example: string;
  cta: string;
}

export const CATEGORY_HELP: Record<ConfigKind, ConfigCategoryHelp> = {
  MONTHLY_MULTIPLIER: {
    label: 'Hệ số mùa vụ theo tháng',
    why: 'Nhân với baseline để ra dự báo sản lượng từng tháng. Phản ánh chu kỳ kinh doanh trong năm.',
    example: 'T12 ×1.45 → tháng 12 dự kiến cao hơn baseline 45%.',
    cta: 'Xem & chỉnh sửa 12 tháng',
  },
  EVENT_MULTIPLIER: {
    label: 'Sự kiện đột biến',
    why: 'Áp dụng khi lịch trùng ngày sự kiện (sale, payday…). Nhân thêm sau hệ số tháng.',
    example: 'Mega Sale ×3.8 → ngày 11.11 dự báo = baseline × hệ_số_tháng × 3.8.',
    cta: 'Xem & chỉnh sửa 6 loại sự kiện',
  },
  UPH_NORM: {
    label: 'Định mức UPH (Units per Hour)',
    why: 'Số đơn/giờ một công nhân xử lý chuẩn — engine dùng để quy đổi sản lượng dự báo ra số FTE cần cho mỗi ca.',
    example: 'UPH = 60 → 1 công nhân xử lý 60 đơn/giờ. Dự báo 4 800 đơn/8h → cần 10 FTE.',
    cta: 'Xem & chỉnh sửa bảng định mức',
  },
  CUTOFF_DAYS: {
    label: 'Số ngày cut-off',
    why: 'Override mặc định: trước sự kiện bao nhiêu ngày thì engine ngưng nhận điều chỉnh forecast.',
    example: 'Cut-off = 3 → khoá điều chỉnh từ D-3 trước ngày sự kiện.',
    cta: 'Xem cấu hình cut-off',
  },
};

export const EVENT_HELP: Record<string, { label: string; when: string; example: string }> = {
  NORMAL: {
    label: 'Ngày thường',
    when: 'Không trùng sự kiện nào — baseline.',
    example: '×1.00 = giữ nguyên dự báo theo tháng.',
  },
  PAYDAY: {
    label: 'Ngày lương',
    when: 'Ngày 25 hàng tháng.',
    example: '×1.15 → đơn ngày lương tăng 15% so với ngày thường cùng tháng.',
  },
  MID_MONTH: {
    label: 'Giữa tháng',
    when: 'Ngày 15 hàng tháng — sale flash các sàn.',
    example: '×1.10 → tăng 10%.',
  },
  MINI_SPIKE: {
    label: 'Sale nhỏ',
    when: '8/3, 20/10, Valentine, lễ nhỏ…',
    example: '×1.30 → tăng 30% so với baseline tháng.',
  },
  DOUBLE_DAY: {
    label: 'Double Day',
    when: '5.5, 6.6, 7.7, 8.8 — sale ngày đôi.',
    example: '×2.00 → gấp đôi baseline.',
  },
  MEGA_SALE: {
    label: 'Mega Sale',
    when: '9.9, 10.10, 11.11, 12.12 — sale lớn nhất năm.',
    example: '×3.80 → gấp gần 4 lần baseline.',
  },
};

export const MONTHLY_HELP: Record<number, string> = {
  1: 'T1 — sau Tết, chậm dần.',
  2: 'T2 — Tết, dip thấp nhất năm.',
  3: 'T3 — phục hồi sau Tết, có 8/3.',
  4: 'T4 — bình thường.',
  5: 'T5 — Double Day 5.5 khởi động mùa sale.',
  6: 'T6 — 6.6 sale, hè bắt đầu.',
  7: 'T7 — 7.7 sale.',
  8: 'T8 — 8.8 sale, back-to-school.',
  9: 'T9 — 9.9 mega sale.',
  10: 'T10 — 10.10 + 20/10.',
  11: 'T11 — đỉnh năm: 11.11 + Black Friday.',
  12: 'T12 — 12.12, year-end, Noel.',
};
